import { useState } from "react";
import { toast } from "sonner";
import { Save, Star, Trash2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

export const DEFAULT_CAPTION = {
  member: { text: "A private dating and adult society… done different. The first 300 members to activate Basic get Premium free for a month. 18+ only.", hashtags: "#Velluto #PrivateSociety #18plus" },
  creator: { text: "Now recruiting creators at Velluto. The first 300 approved creators keep 100% of their earnings for 3 months. 18+ only.", hashtags: "#Velluto #CreatorsWanted #CamModel #18plus" },
};

export const tidyTags = (s) => (s || "").split(/[\s,]+/).filter(Boolean).map((t) => `#${t.replace(/^#+/, "")}`).join(" ");
export const fullCaption = (c) => [c.text.trim(), tidyTags(c.hashtags)].filter(Boolean).join("\n\n");

const field = "w-full bg-black/30 border border-white/10 rounded-xl p-2.5 text-xs focus:border-gold outline-none";

export function CaptionEditor({ adKey, kind, value, onChange, store, testPrefix }) {
  const [busy, setBusy] = useState(false);
  const [showFavs, setShowFavs] = useState(false);
  const saved = store.captions[adKey] || DEFAULT_CAPTION[kind];
  const dirty = value.text !== saved.text || value.hashtags !== saved.hashtags;
  const save = async () => {
    setBusy(true);
    const tidy = { ...value, hashtags: tidyTags(value.hashtags) };
    onChange(tidy);
    try { store.setCaption(adKey, (await api.put(`/admin/ad-captions/${adKey}`, tidy)).data); toast.success("Caption saved for this ad"); }
    catch { toast.error("Couldn't save caption"); }
    setBusy(false);
  };
  const favourite = async () => {
    try { store.addFav((await api.post("/admin/ad-captions/favourites", { ...value, hashtags: tidyTags(value.hashtags) })).data); toast.success("Added to favourites"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save favourite"); }
  };
  const remove = async (id) => { await api.delete(`/admin/ad-captions/favourites/${id}`); store.removeFav(id); };
  return (
    <div data-testid={`${testPrefix}-caption`} className="space-y-2">
      <textarea data-testid={`${testPrefix}-caption-text`} value={value.text} maxLength={1000} rows={3} onChange={(e) => onChange({ ...value, text: e.target.value })} className={field} placeholder="Caption" />
      <input data-testid={`${testPrefix}-caption-tags`} value={value.hashtags} maxLength={400} onChange={(e) => onChange({ ...value, hashtags: e.target.value })} onBlur={() => onChange({ ...value, hashtags: tidyTags(value.hashtags) })} className={field} placeholder="#hashtags" />
      <div className="flex flex-wrap items-center gap-1.5">
        <button data-testid={`${testPrefix}-caption-save`} disabled={busy || !dirty} onClick={save} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.12em] disabled:opacity-40"><Save size={11} /> Save</button>
        <button data-testid={`${testPrefix}-caption-fav`} onClick={favourite} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 text-[10px] uppercase tracking-[0.12em] text-text-muted hover:text-gold"><Star size={11} /> Favourite</button>
        <button data-testid={`${testPrefix}-caption-favs`} onClick={() => setShowFavs(!showFavs)} className="px-3 py-1.5 rounded-full border border-white/10 text-[10px] uppercase tracking-[0.12em] text-text-muted hover:text-gold">Favourites ({store.favourites.length})</button>
        <button data-testid={`${testPrefix}-caption-reset`} onClick={() => onChange(DEFAULT_CAPTION[kind])} title="Back to the ready-made caption" className="p-1.5 rounded-full border border-white/10 text-text-muted hover:text-gold"><RotateCcw size={11} /></button>
      </div>
      {showFavs && (
        <div data-testid={`${testPrefix}-caption-fav-list`} className="max-h-48 overflow-y-auto space-y-1.5 rounded-xl border border-white/8 p-2">
          {!store.favourites.length && <p className="text-[11px] text-text-muted p-1">No favourites yet. Tap Favourite to keep a caption.</p>}
          {store.favourites.map((f) => (
            <div key={f.id} className="flex items-start gap-2 rounded-lg hover:bg-white/5 p-1.5">
              <button data-testid={`${testPrefix}-fav-use-${f.id}`} onClick={() => { onChange({ text: f.text, hashtags: f.hashtags }); setShowFavs(false); }} className="flex-1 text-left text-[11px] text-text-main line-clamp-3">
                {f.text} <span className="text-gold">{tidyTags(f.hashtags)}</span>
              </button>
              <button data-testid={`${testPrefix}-fav-delete-${f.id}`} onClick={() => remove(f.id)} className="text-text-muted hover:text-red-300 p-1"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
